/**
 * Formats seconds into MM:SS or HH:MM:SS format
 */
export function formatTime(totalSeconds: number, includeHours = false): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (includeHours || hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
}

/**
 * Formats seconds into a human-readable duration (e.g., "2h 30m", "45m", "30s")
 */
export function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (seconds > 0 && hours === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ') || '0s';
}

/**
 * Parses a time string (MM:SS or HH:MM:SS) into total seconds
 */
export function parseTimeString(timeString: string): number {
  const parts = timeString.split(':').map(part => parseInt(part, 10));
  
  if (parts.length === 2) {
    // MM:SS format
    const [minutes, seconds] = parts;
    return (minutes * 60) + seconds;
  } else if (parts.length === 3) {
    // HH:MM:SS format
    const [hours, minutes, seconds] = parts;
    return (hours * 3600) + (minutes * 60) + seconds;
  }
  
  return 0;
}

/**
 * Converts minutes to seconds
 */
export function minutesToSeconds(minutes: number): number {
  return minutes * 60;
}

/**
 * Converts seconds to minutes (rounded)
 */
export function secondsToMinutes(seconds: number): number {
  return Math.round(seconds / 60);
}

/**
 * Gets the current time as a formatted string
 */
export function getCurrentTimeString(): string {
  const now = new Date();
  return now.toLocaleTimeString();
}

/**
 * Calculates elapsed time between two dates
 */
export function getElapsedTime(startTime: Date, endTime?: Date): number {
  const end = endTime || new Date();
  return Math.floor((end.getTime() - startTime.getTime()) / 1000);
}

/**
 * Formats elapsed time for display
 */
export function formatElapsedTime(startTime: Date, endTime?: Date): string {
  const elapsed = getElapsedTime(startTime, endTime);
  return formatTime(elapsed, true);
}

/**
 * Calculates estimated end time based on remaining seconds
 */
export function getEstimatedEndTime(secondsRemaining: number): Date {
  const now = new Date();
  return new Date(now.getTime() + (secondsRemaining * 1000));
}

/**
 * Formats a date to a readable string
 */
export function formatDateTime(date: Date): string {
  return date.toLocaleString();
}

/**
 * Gets time until next break
 */
export function getTimeUntilNextBreak(
  currentRound: number,
  rounds: Array<{ minutes: number; breakMinutes: number }>,
  secondsRemaining: number
): number | null {
  // Find the next round with a break
  for (let i = currentRound - 1; i < rounds.length; i++) {
    const round = rounds[i];
    if (round.breakMinutes > 0) {
      if (i === currentRound - 1) {
        // Break is after current round
        return secondsRemaining;
      } else {
        // Add up time until that break
        let totalSeconds = secondsRemaining;
        for (let j = currentRound; j < i; j++) {
          totalSeconds += rounds[j].minutes * 60;
        }
        return totalSeconds;
      }
    }
  }
  
  return null; // No more breaks
}